import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Alert,
  Box,
  Card,
  CardMedia,
  CircularProgress,
  FormHelperText,
  Link as MuiLink,
  Typography
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import React, { useEffect, useState } from 'react';
import ApiClient from '../config/ApiClient';
import { PublicOrganization } from '../types/intakeSurvey';

interface OrganizationDirectoryProps {
  value?: number | string | null;
  onChange: (orgId: number) => void;
  error?: string;
}

interface OrganizationsListResponse {
  results?: PublicOrganization[];
}

// Build the /request-access URL on a sibling tenant subdomain by swapping the
// leading host label (works across local/stage/prod and preserves the port).
function buildOrgRequestAccessUrl(slug: string): string {
  const { protocol, host } = window.location;
  const parts = host.split('.');
  if (parts.length <= 1) {
    return `${protocol}//${host}/dashboard`;
  }
  parts[0] = slug;
  return `${protocol}//${parts.join('.')}/dashboard`;
}

const OrganizationDirectory: React.FC<OrganizationDirectoryProps> = ({ value, onChange, error }) => {
  const theme = useTheme();
  const [orgs, setOrgs] = useState<PublicOrganization[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      const res = await ApiClient.get<PublicOrganization[] | OrganizationsListResponse>('/api/organizations/');
      if (cancelled) return;
      if (res.success && res.data) {
        const list = Array.isArray(res.data) ? res.data : res.data.results ?? [];
        setOrgs(list);
      } else {
        setLoadError(res.error ?? 'Unable to load organizations.');
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadError) {
    return <Alert severity="error">{loadError}</Alert>;
  }

  if (orgs.length === 0) {
    return (
      <Alert severity="info">
        No organizations are listed yet. Enter the email or phone of the person who invited you and submit, and
        we&apos;ll help you find your program.
      </Alert>
    );
  }

  const selectedId = value === undefined || value === null || value === '' ? null : Number(value);

  return (
    <Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)' },
          gap: 2,
        }}
      >
        {orgs.map((org) => {
          const isSelected = selectedId === org.id;
          return (
            <Card
              key={org.id}
              variant="outlined"
              data-testid="request-access-org-card"
              sx={{
                position: 'relative',
                textAlign: 'center',
                borderColor: isSelected ? 'secondary.main' : 'divider',
                borderWidth: isSelected ? 3 : 1,
                backgroundColor: isSelected
                  ? alpha(theme.palette.secondary.main, 0.8)
                  : 'transparent',
                p: 2, pb: 0
              }}
            >
              <Box sx={{
                display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                alignItems: 'center', gap: 8
              }} >


                <Box
                  onClick={() => onChange(org.id)}
                  data-attr="request-access_select-org"
                  data-custom-id={String(org.id)}
                  style={{ cursor: 'pointer' }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {org.name}
                  </Typography>
                </Box>


                <CardMedia
                  onClick={() => onChange(org.id)}
                  component="img"
                  src={theme.palette.mode === 'dark' ? org.logo_dark ?? org.logo ?? undefined : org.logo ?? org.logo_dark ?? undefined}
                  alt={org.name}
                  sx={{ objectFit: 'contain' }}
                />

                <Box>
                  <MuiLink
                    href={buildOrgRequestAccessUrl(org.slug)}
                    underline="hover"
                    variant="caption"
                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                  >
                    Visit site <OpenInNewIcon sx={{ fontSize: 14 }} />
                  </MuiLink>
                </Box>
              </Box>

            </Card>
          );
        })}
      </Box>
      {error && <FormHelperText error>{error}</FormHelperText>}
    </Box>
  );
};

export default OrganizationDirectory;
